import React, { useState, useEffect } from "react";
import { Formik, Form, useField } from "formik";
import * as Yup from "yup";
import emailjs from "emailjs-com";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Button, Headline, InlineLink } from "./ui";
import { Paragraph } from "./ui/Typography";
import SmartImage from "./SmartImage";

const MyTextInput = ({ label, darkMode, ...props }) => {
  const [field, meta] = useField(props);
  return (
    <div className="mb-4">
      <label
        htmlFor={props.id || props.name}
        className={`block text-sm font-medium mb-2 ${
          darkMode ? "text-[var(--color-primary)]" : "text-gray-700"
        }`}
      >
        {label}
      </label>
      <input
        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          meta.touched && meta.error
            ? "border-red-500"
            : darkMode
            ? "border-gray-600 bg-[var(--color-secondary)] text-[var(--color-primary)] placeholder-gray-400"
            : "border-gray-300 bg-white text-gray-900"
        }`}
        {...field}
        {...props}
      />
      {meta.touched && meta.error ? (
        <div className="text-red-500 text-sm mt-1">{meta.error}</div>
      ) : null}
    </div>
  );
};

const MyCheckbox = ({ children, darkMode, ...props }) => {
  const [field, meta] = useField({ ...props, type: "checkbox" });
  return (
    <div className="mb-4">
      <label className="flex items-start">
        <input
          type="checkbox"
          {...field}
          {...props}
          className="mt-1 mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <span
          className={darkMode ? "text-[var(--color-primary)]" : "text-gray-700"}
        >
          {children}
        </span>
      </label>
      {meta.touched && meta.error ? (
        <div className="text-red-500 text-sm mt-1">{meta.error}</div>
      ) : null}
    </div>
  );
};

const CallToActionWithModalV2 = ({
  headline,
  paragraph,
  darkMode = false,
  margin = true,
  className = "",
}) => {
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showModal]);

  return (
    <div className={className}>
      {/* CTA Section with matching typography */}
      <div>
        <Headline
          variant="splitscreen-heading"
          color="grayDark"
          animate={false}
        >
          {headline}
        </Headline>
        <Paragraph variant="large" color="gray" className="mb-6">
          {paragraph}
        </Paragraph>
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            as="a"
            href="https://wa.me/4915756850714"
            target="_blank"
            rel="noopener noreferrer"
            variant="secondary"
            size="medium"
          >
            Jetzt über WhatsApp
          </Button>
          <Button
            onClick={() => setShowModal(true)}
            variant="primary"
            size="medium"
            style={
              darkMode
                ? {
                    backgroundColor: "white",
                    color: "var(--color-primary)",
                  }
                : {
                    backgroundColor: "var(--color-primary)",
                    color: "var(--color-secondary)",
                  }
            }
          >
            Rückruf anfordern
          </Button>
        </div>
      </div>

      {/* Modal - exact copy from original */}
      {showModal && (
        <div className="fixed inset-0 mt-[50px] z-50 flex items-center justify-center bg-[var(--color-primary)] p-4">
          <div
            className="rounded-sm p-6 w-full max-w-md shadow-lg relative text-[var(--color-primary)]"
            style={{ backgroundColor: "var(--color-secondary-dark)" }}
          >
            <Headline variant="h4" className="mb-6 text-left" color="primary">
              Rückruf anfordern
            </Headline>
            <SmartImage
              src="gallery/workshop/workshop-outside_uw6i86"
              alt="WTM Sportwagen Werkstatt"
              width={400}
              height={200}
              className="w-full h-0 sm:h-40 object-cover rounded-sm mb-6"
            />
            <Formik
              initialValues={{
                name: "",
                phone: "",
                time: "",
                consent: false,
              }}
              validationSchema={Yup.object({
                name: Yup.string()
                  .max(50, "Name darf maximal 50 Zeichen lang sein")
                  .required("Pflichtfeld"),
                phone: Yup.string()
                  .matches(
                    /^[0-9\s\-\+\(\)]+$/,
                    "Bitte geben Sie eine gültige Telefonnummer ein"
                  )
                  .min(5, "Telefonnummer muss mindestens 5 Zeichen lang sein")
                  .required("Pflichtfeld"),
                consent: Yup.boolean()
                  .oneOf([true], "Bitte zustimmen")
                  .required(
                    "Zustimmung zur Datenschutzerklärung ist erforderlich"
                  ),
              })}
              onSubmit={(values, { setSubmitting, resetForm }) => {
                // Show success toast immediately
                toast.success(
                  "Vielen Dank für Ihre Anfrage! Wir melden uns unverzüglich bei Ihnen."
                );

                // Send email via EmailJS
                emailjs
                  .send(
                    "service_t0w7lhk",
                    "template_nbeag76",
                    {
                      from_name: values.name,
                      phone: values.phone,
                      time: values.time,
                      message: `Rückruf angefordert für ${
                        values.time || "keine spezifische Zeit angegeben"
                      }`,
                    },
                    "7yrBfJRP1uVs_QHMZ"
                  )
                  .then(
                    (result) => {
                      resetForm();
                      setShowModal(false);
                    },
                    (error) => {
                      console.error("Email sending failed:", error);
                      toast.error(
                        "Ein Fehler ist aufgetreten, bitte kontaktieren Sie uns direkt per Telefon oder WhatsApp."
                      );
                    }
                  )
                  .finally(() => {
                    setSubmitting(false);
                  });
              }}
            >
              {({
                isSubmitting,
                isValid,
                values,
                setFieldValue,
                setTouched,
              }) => (
                <Form>
                  <MyTextInput
                    label="Name"
                    name="name"
                    type="text"
                    placeholder="Ihr vollständiger Name"
                    className="mb-4"
                    darkMode={true}
                  />

                  <MyTextInput
                    label="Telefonnummer"
                    name="phone"
                    type="tel"
                    placeholder="0179 1234 5678"
                    className="mb-4"
                    darkMode={true}
                  />

                  <div className="flex flex-col gap-2">
                    <p className="text-sm text-[var(--color-primary)] text-left">
                      Bevorzugte Uhrzeit (optional)
                    </p>
                    <div className="flex w-full pb-4">
                      {["Vormittags", "Nachmittags", "Abends"].map(
                        (label, index) => (
                          <button
                            key={label}
                            type="button"
                            onClick={() => setFieldValue("time", label)}
                            className={`px-4 py-2 border text-sm ${
                              values.time === label
                                ? "text-[var(--color-secondary)] border-[var(--color-primary)]"
                                : "text-[var(--color-primary)] border-gray-600"
                            } ${
                              index === 0
                                ? "rounded-l"
                                : index === 2
                                ? "rounded-r"
                                : ""
                            } ${index > 0 ? "-ml-px" : ""}`}
                            style={{
                              backgroundColor:
                                values.time === label
                                  ? "var(--color-primary)"
                                  : "var(--color-secondary)",
                            }}
                          >
                            {label}
                          </button>
                        )
                      )}
                    </div>
                  </div>

                  <MyCheckbox name="consent" darkMode={true}>
                    <p className="text-sm text-[var(--color-primary)] text-left">
                      Ich stimme der Verarbeitung meiner Daten zur Bearbeitung
                      der Anfrage zu.{" "}
                      <InlineLink to="/datenschutz" variant="primary">
                        Datenschutzerklärung
                      </InlineLink>
                    </p>
                  </MyCheckbox>

                  <div className="flex gap-4 mt-6">
                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      disabled={isSubmitting || !isValid}
                      className="flex-1"
                      style={{
                        backgroundColor: "var(--color-primary)",
                        color: "var(--color-secondary)",
                      }}
                    >
                      {isSubmitting ? "Wird gesendet..." : "Rückruf anfordern"}
                    </Button>
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      disabled={isSubmitting}
                      className="px-4 py-2 text-[var(--color-primary)] bg-transparent border border-[var(--color-primary)] rounded hover:bg-[var(--color-primary)] hover:text-[var(--color-secondary)] transition-colors"
                    >
                      Abbrechen
                    </button>
                  </div>
                </Form>
              )}
            </Formik>

            {/* Close button */}
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-[var(--color-primary)] hover:text-[var(--color-secondary)] text-2xl font-bold"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default CallToActionWithModalV2;
